import React, { useState, useEffect } from 'react';
import { inverterTrendService } from '../../services/inverterTrendService';

interface TrinomicLossOverlayProps {
  inverterId: string;
  date?: string;
  isVisible: boolean;
}

interface TrinomicLossData {
  thermal_loss: number;
  mismatch_loss: number;
  shading_loss: number;
  total_loss: number;
  date: string;
}

const TrinomicLossOverlay: React.FC<TrinomicLossOverlayProps> = ({
  inverterId,
  date,
  isVisible
}) => {
  const [lossData, setLossData] = useState<TrinomicLossData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isVisible) {
      loadLossData();
    }
  }, [inverterId, date, isVisible]);

  const loadLossData = async () => {
    try {
      setLoading(true);
      // Mock data for visualization
      const mockLossData: TrinomicLossData = {
        thermal_loss: 2.5 + Math.random() * 1,
        mismatch_loss: 1.8 + Math.random() * 0.5,
        shading_loss: 0.7 + Math.random() * 0.3,
        total_loss: 5.0 + Math.random() * 1.5,
        date: date || new Date().toISOString().split('T')[0]
      };
      setLossData(mockLossData);
      
      // Uncomment to use real API
      // const response = await inverterTrendService.getTrinomicLoss(inverterId, date);
      // setLossData(response);
    } catch (error) {
      console.error('Error loading trinomic loss data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isVisible) return null;

  if (loading) {
    return (
      <div className="bg-white p-4 rounded-lg border">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-2">
            <div className="h-3 bg-gray-200 rounded"></div>
            <div className="h-3 bg-gray-200 rounded w-5/6"></div>
            <div className="h-3 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!lossData) return null;

  const lossComponents = [
    { label: 'Thermal Loss', value: lossData.thermal_loss, color: 'bg-red-500' },
    { label: 'Mismatch Loss', value: lossData.mismatch_loss, color: 'bg-orange-500' },
    { label: 'Shading Loss', value: lossData.shading_loss, color: 'bg-yellow-500' }
  ];

  return (
    <div className="bg-white p-4 rounded-lg border">
      <h4 className="text-md font-medium mb-3">
        Trinomic Loss Breakdown - {lossData.date}
      </h4>
      
      <div className="space-y-3">
        {lossComponents.map((component, index) => (
          <div key={index} className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded ${component.color}`}></div>
              <span className="text-sm">{component.label}</span>
            </div>
            <span className="text-sm font-medium">{component.value.toFixed(2)}%</span>
          </div>
        ))}
        
        <div className="border-t pt-2 mt-3">
          <div className="flex items-center justify-between font-medium">
            <span>Total Loss</span>
            <span className="text-red-600">{lossData.total_loss.toFixed(2)}%</span>
          </div>
        </div>
      </div>
      
      {/* Visual breakdown */}
      <div className="mt-4">
        <div className="flex h-4 rounded overflow-hidden">
          <div 
            className="bg-red-500" 
            style={{ width: `${(lossData.thermal_loss / lossData.total_loss) * 100}%` }}
          ></div>
          <div 
            className="bg-orange-500" 
            style={{ width: `${(lossData.mismatch_loss / lossData.total_loss) * 100}%` }}
          ></div>
          <div 
            className="bg-yellow-500" 
            style={{ width: `${(lossData.shading_loss / lossData.total_loss) * 100}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default TrinomicLossOverlay;